import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUserAttributes, updateUserAttributes } from "aws-amplify/auth";
import { useAuth } from "../auth/AuthProvider.js";
import { Button } from "../ui/Button.js";
import { Card, Divider, SectionLabel } from "../ui/Card.js";

/**
 * Phone numbers are stored in Cognito as E.164 (`+15555550100`). The form
 * accepts user-friendly input and we normalize on save. Cognito will reject
 * anything that fails E.164 validation server-side.
 */
function normalizePhone(input: string): string {
  const trimmed = input.trim();
  if (trimmed.length === 0) return "";
  if (trimmed.startsWith("+")) {
    return "+" + trimmed.slice(1).replace(/[^0-9]/g, "");
  }
  const digits = trimmed.replace(/[^0-9]/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return trimmed;
}

export function AppAccount() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const attrs = await fetchUserAttributes();
        if (cancelled) return;
        const current = attrs["custom:phone"] ?? "";
        setPhone(current);
        setOriginalPhone(current);
      } catch {
        // Non-fatal; just leave the form empty.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSignOut() {
    await signOut();
    navigate("/", { replace: true });
  }

  async function onSavePhone(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const normalized = normalizePhone(phone);
      await updateUserAttributes({
        userAttributes: { "custom:phone": normalized },
      });
      setPhone(normalized);
      setOriginalPhone(normalized);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save phone");
    } finally {
      setSaving(false);
    }
  }

  const email = user?.signInDetails?.loginId ?? user?.username;
  const phoneChanged = normalizePhone(phone) !== originalPhone;

  return (
    <section className="mx-auto max-w-md px-4 pt-6">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Account</h1>

      <div className="mt-6 space-y-3">
        <SectionLabel>Profile</SectionLabel>
        <Card className="divide-y divide-slate-100">
          <div className="px-4 py-3">
            <p className="text-xs font-medium text-slate-500">Signed in as</p>
            <p className="mt-0.5 text-sm font-medium text-slate-900">{email}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs font-medium text-slate-500">User ID</p>
            <p className="mt-0.5 break-all font-mono text-xs text-slate-700">{user?.userId}</p>
          </div>
        </Card>
      </div>

      <div className="mt-6 space-y-3">
        <SectionLabel>Contact phone</SectionLabel>
        <Card>
          <form onSubmit={onSavePhone} className="space-y-3 px-4 py-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">Phone number</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setSaved(false);
                }}
                inputMode="tel"
                autoComplete="tel"
                placeholder="+1 555 555 0100"
                disabled={loading || saving}
                className="block min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600 disabled:opacity-60"
              />
              <p className="mt-1 text-xs text-slate-500">
                Used only to coordinate your service requests.
              </p>
            </label>
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            {saved && !error && <p className="text-sm text-teal-700">Saved.</p>}
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={loading || saving || !phoneChanged}
            >
              {saving ? "Saving…" : "Save phone"}
            </Button>
          </form>
        </Card>
      </div>

      <Divider className="my-8" />

      <Button variant="secondary" size="lg" fullWidth onClick={onSignOut}>
        Sign out
      </Button>
    </section>
  );
}
